import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Share2, Sheet as SheetIcon, Download, Filter, ExternalLink } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../../components/ui/sheet';
import { MultiSelect } from '../MultiSelect';
import { exportShortlistCsv } from '../exportCsv';
import { BriefCreatorList } from './BriefCreatorList';
import { StatusSelect } from './SavedBriefsTable';
import { MarkupField, markupLabel } from './MarkupField';
import { api, BTN, BTN_PRIMARY, budgetText, dateText, errMsg, inr } from './briefApi';

const exportedText = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : '—');

const Meta = ({ label, value }) => (
  <div><div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-400">{label}</div><div className="text-sm text-[#0F0F0F] dark:text-white">{value || '—'}</div></div>
);

const SharePanel = ({ brief, onShared }) => {
  const [tms, setTms] = useState([]);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/tms').then((d) => setTms(d.tms)).catch(() => {}); }, []);
  const label = (t) => `${t.name} (${t.email})`;
  const share = async () => {
    setBusy(true);
    try {
      const ids = tms.filter((t) => picked.includes(label(t))).map((t) => t.id);
      const d = await api.post(`/${brief.id}/share`, { tm_ids: ids });
      toast.success(`Shared with ${d.shared_with.map((t) => t.name).join(', ')}`);
      setPicked([]);
      onShared();
    } catch (e) {
      toast.error(errMsg(e, 'Could not share'));
    } finally {
      setBusy(false);
    }
  };
  const sharedNames = tms.filter((t) => (brief.shared_with || []).includes(t.id)).map((t) => t.name);
  return (
    <div className="rounded-xl border border-black/[0.08] dark:border-white/5 p-3 space-y-2" data-testid="brief-share-panel">
      <div className="text-xs font-semibold uppercase tracking-[0.08em] text-[#0F0F0F] dark:text-white flex items-center gap-1.5"><Share2 size={12} /> Share with TMs</div>
      <div className="flex gap-2 items-start">
        <div className="flex-1"><MultiSelect value={picked} onChange={setPicked} options={tms.filter((t) => !(brief.shared_with || []).includes(t.id)).map(label)} placeholder="Pick talent managers" clearLabel="Clear all" testid="brief-share-tms" /></div>
        <button onClick={share} disabled={busy || !picked.length} className={BTN_PRIMARY} data-testid="brief-share-btn">{busy ? <Loader2 size={13} className="animate-spin" /> : <Share2 size={13} />} Share</button>
      </div>
      <p className="text-[11px] text-[#6B6B6B] dark:text-zinc-400" data-testid="brief-shared-with">{sharedNames.length ? `Shared with: ${sharedNames.join(', ')}` : 'Not shared yet'}</p>
    </div>
  );
};

export const BriefDetailSheet = ({ briefId, onClose, onChanged, onLoad }) => {
  const [d, setD] = useState(null);
  const [sheetBusy, setSheetBusy] = useState(null);
  const load = useCallback(() => {
    if (briefId) api.get(`/${briefId}`).then(setD).catch((e) => toast.error(errMsg(e, 'Could not open brief')));
  }, [briefId]);
  useEffect(() => { setD(null); load(); }, [load]);

  const refresh = () => { load(); onChanged(); };
  const remove = (list) => async (c) => {
    const msg = list === 'tm' ? `Remove ${c.name} (added by ${c.added_by_name || 'a TM'}) from this brief? The TM will see it removed too.` : `Remove ${c.name} from the shortlist?`;
    if (!window.confirm(msg)) return;
    try {
      await api.del(`/${briefId}/creators/${c.creator_id}?list_name=${list}`);
      toast.success(`Removed ${c.name}`);
      refresh();
    } catch (e) { toast.error(errMsg(e, 'Could not remove')); }
  };
  const setStatus = async (s) => {
    try { await api.patch(`/${briefId}/status`, { status: s }); toast.success(`Brief ${s}`); refresh(); } catch (e) { toast.error(errMsg(e, 'Could not update status')); }
  };
  const tmIds = new Set((d?.tm_additions || []).map((c) => c.creator_id));
  const combined = d ? [...d.shortlisted.filter((c) => !tmIds.has(c.creator_id)), ...d.tm_additions.map((c) => ({ ...c, manager_note: `Added by TM ${c.added_by_name || ''}` }))] : [];
  const toSheet = async (which) => {
    setSheetBusy(which);
    const win = window.open('', '_blank');
    try {
      const r = await api.post(`/${briefId}/export-sheet?which=${which}`);
      const name = which === 'internal' ? 'Internal' : 'Client';
      const example = which === 'client' && r.sample_cost ? ` (e.g. ${inr(r.sample_cost)} → ${inr(r.sample_client_price)})` : '';
      const desc = which === 'client' ? `${r.rows} creators · prices at ${markupLabel(r.markup)}${example}` : `${r.rows} creators · actual creator costs`;
      if (r.status === 'recreated') toast.warning(`Previous ${name} sheet was missing — created a new one`, { description: desc });
      else toast.success(`${name} sheet ${r.status} — opening it`, { description: desc });
      const { which: _w, url, status, rows, markup, sample_cost, sample_client_price, ...fields } = r;
      setD((x) => ({ ...x, ...fields }));
      onChanged();
      if (win) win.location.href = url; else window.open(url, '_blank', 'noopener');
    } catch (e) {
      if (win) win.close();
      toast.error(errMsg(e, 'Could not export sheet — check Google connection'));
    } finally { setSheetBusy(null); }
  };

  return (
    <Sheet open={!!briefId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-6xl overflow-y-auto" data-testid="brief-detail-sheet">
        {!d ? <div className="flex items-center gap-2 text-sm py-10"><Loader2 size={14} className="animate-spin" /> Loading brief…</div> : (
          <div className="space-y-5">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-3" data-testid="brief-detail-title">{d.brand_name} · {d.deliverable} <StatusSelect value={d.status} onChange={setStatus} testid="brief-detail-status" /></SheetTitle>
              <SheetDescription>{d.objective || 'No objective set'}</SheetDescription>
            </SheetHeader>
            <div className="grid grid-cols-3 gap-3">
              <Meta label="Budget / creator" value={budgetText(d)} />
              <Meta label="Deadline" value={dateText(d.deadline)} />
              <Meta label="Niche" value={(d.niche || []).join(', ')} />
              <Meta label="City / State" value={[...(d.city || []), ...(d.state || [])].join(', ')} />
              <Meta label="Followers" value={d.followers_min || d.followers_max ? `${d.followers_min ?? 0} – ${d.followers_max ?? '∞'}` : ''} />
              <Meta label="Quality tier" value={(d.quality_tier || []).join(', ')} />
            </div>
            {d.notes && <p className="text-xs text-[#4B5563] dark:text-zinc-300 whitespace-pre-wrap"><span className="font-semibold">Notes:</span> {d.notes}</p>}
            <SharePanel brief={d} onShared={refresh} />
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm text-[#0F0F0F] dark:text-white" data-testid="brief-detail-total">Combined total: <span className="font-bold text-[#FF451A]">{combined.length}</span> creators</div>
              <div className="flex gap-2 flex-wrap items-center">
                <MarkupField briefId={d.id} value={d.client_markup} onSaved={(m) => setD((x) => ({ ...x, client_markup: m }))} />
                <button onClick={() => { onLoad(d); onClose(); }} className={BTN} data-testid="brief-detail-load-btn"><Filter size={13} /> Load filters</button>
                <button onClick={() => exportShortlistCsv(combined, d.brand_name, d.cost_columns)} disabled={!combined.length} className={BTN} data-testid="brief-detail-csv-btn"><Download size={13} /> Export CSV</button>
                {[['internal', 'Export Internal', 'bg-[#0F0F0F] dark:bg-white text-white dark:text-[#0F0F0F] hover:opacity-90'], ['client', 'Export Client', 'bg-[#0F9D58] hover:bg-[#0B8043] text-white']].map(([w, label, cls]) => (
                  <button key={w} onClick={() => toSheet(w)} disabled={!combined.length || !!sheetBusy} className={`rounded-full px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40 transition-colors ${cls}`} data-testid={`brief-export-${w}-btn`}>
                    {sheetBusy === w ? <Loader2 size={13} className="animate-spin" /> : <SheetIcon size={13} />} {label}
                  </button>
                ))}
              </div>
            </div>
            {(d.sheet_url || d.internal_sheet_url) && (
              <div className="flex flex-col items-end gap-1 -mt-3 text-xs" data-testid="brief-sheet-info">
                {d.internal_sheet_url && (
                  <div className="flex items-center gap-3">
                    <a href={d.internal_sheet_url} target="_blank" rel="noopener noreferrer" title="Actual creator costs — StrawHat only, never share" className="inline-flex items-center gap-1 font-semibold text-[#0F0F0F] dark:text-white hover:underline" data-testid="brief-view-internal-sheet-link">View Internal Sheet <ExternalLink size={11} /></a>
                    <span className="text-[#6B6B6B] dark:text-zinc-400" data-testid="brief-internal-last-exported">Last exported: {exportedText(d.internal_sheet_exported_at || d.sheet_exported_at)}</span>
                  </div>
                )}
                {d.sheet_url && (
                  <div className="flex items-center gap-3 flex-wrap justify-end">
                    {(d.sheet_markup ?? 1.3) !== d.client_markup && <span className="rounded-full px-2 py-0.5 bg-amber-500/10 text-amber-700 dark:text-amber-400 font-medium" data-testid="brief-markup-changed-note">Markup changed ({markupLabel(d.sheet_markup ?? 1.3)} → {markupLabel(d.client_markup)}) — Export Client to update prices</span>}
                    <a href={d.sheet_url} target="_blank" rel="noopener noreferrer" title="Marked-up prices — safe to share with the client" className="inline-flex items-center gap-1 font-semibold text-[#0F9D58] hover:underline" data-testid="brief-view-client-sheet-link">View Client Sheet <ExternalLink size={11} /></a>
                    <span className="text-[#6B6B6B] dark:text-zinc-400" data-testid="brief-client-last-exported">Last exported: {exportedText(d.client_sheet_exported_at || d.sheet_exported_at)}</span>
                  </div>
                )}
              </div>
            )}
            <BriefCreatorList title="Shortlisted" rows={d.shortlisted} costCols={d.cost_columns} markup={d.client_markup} onRemove={remove('shortlisted')} testid="brief-shortlisted" empty="No creators yet — load filters, run suggestions and click Save to Brief." />
            {(d.kind !== 'saved' || d.tm_additions.length > 0) && <BriefCreatorList title="Added by TMs" rows={d.tm_additions} costCols={d.cost_columns} markup={d.client_markup} onRemove={remove('tm')} showAddedBy testid="brief-tm-additions" empty="No TM additions yet. Share the brief with TMs so they can add their exclusive creators." />}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
